/**
 * HouseholdProductDetailPage — the HouseholdProduct settings surface.
 *
 * Reached by tapping a product row in ProductsPage, or as a layover from a
 * planning-list item (route `/products/:id`). Built to the planning-shopping
 * prototype (`#s-settings` mobile / `#stage-web` desktop): one scrolling column
 * behind a sticky compact header, in prototype order (T27.2.7.10) —
 *   Basics (How many · Size · How often) → Product preferences → Brand
 *   preferences → Store preferences → collapsed extras (Quantity & packaging,
 *   Expected price, More details).
 *
 * Composition: <HouseholdProductSettings> owns the prototype order — the
 * always-open Basics group (Size + How often), then the foldable Brand and Store
 * preference sections. The detail page injects two pieces: the "How many to add"
 * stepper (`basicsLeading`, because the page form persists `default_quantity`)
 * and the <SystemProductPicker> (`productPreferencesSlot`, so Product
 * preferences sits between Basics and Brand preferences). The "occasional
 * extras" (packaging / expected price / notes / tags) edit through this page's
 * form, each a `details.setgroup`.
 *
 * Flat blocks only — there is no Card-in-Card: the picker and the settings
 * sections carry their own border/radius chrome, and the extras are bordered
 * `<details>` groups. There is no LinkedOffersEditor on this surface — the
 * prototype's model is auto-follow offers via Products-you-want + Where-to-buy.
 *
 * Deep-link: a `section` query param (e.g. `?section=brands`) scrolls to + briefly
 * highlights the matching `[data-section]` block; with no target the surface
 * opens at the top. Dynamic back: a `from` query param (`list` | `products`)
 * returns to the caller; default Products.
 *
 * Desktop: at the app's desktop breakpoint (apps/web/src/responsive.css) the
 * section groups reflow into the prototype's `.sp-cols` / `.desk-grid` /
 * `.desk-grid3` grids; the extras open by default.
 *
 * Category + image are surfaced as a "coming soon" stop (no picker is wired yet),
 * not raw id inputs. The old read-only retailer block is removed — the editable
 * Where-to-buy control in <HouseholdProductSettings> supersedes it.
 *
 * All styles flow from tokens (CP5). No hex, no raw px literals.
 */

import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type KeyboardEvent,
} from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useHouseholdProduct } from '@shopista/shared/api/hooks';
import { apiErrorMessage } from '@shopista/shared/api';
import type {
  HouseholdProductWithDetails,
  UpdateHouseholdProductRequest,
} from '@shopista/shared/types';
import type { PriceCurrency } from '@shopista/shared/types';
import { Button, Card, Icon, Input, Spinner } from '../../components';
import { useOrchestrators } from '../../stores/orchestrator-context';
import { usePublishLayoverTitle } from '../../stores/useLayoverTitle';
import { routes } from '../../routes/paths';
import { SystemProductPicker } from './SystemProductPicker';
import { HouseholdProductSettings } from './HouseholdProductSettings';
import {
  setgroupStyle,
  detailsSummaryStyle,
  mutedStyle,
  stepperStyle,
  stepperBtnStyle,
  stepperValueStyle,
} from './settingsStyles';

const CURRENCIES: PriceCurrency[] = ['CAD', 'USD', 'EUR', 'GBP', 'MXN'];

// Deep-link section targets → the heading shown in the brief flash, for the
// page-owned extras. (Picker/settings sections carry their own `data-section`.)
const SECTION_KEYS = [
  'basics',
  'products',
  'brands',
  'where',
  'size',
  'howoften',
  'price',
  'details',
] as const;
type SectionKey = (typeof SECTION_KEYS)[number];

function isSectionKey(value: string | null): value is SectionKey {
  return value != null && (SECTION_KEYS as readonly string[]).includes(value);
}

const pageStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-4)',
};

const stickyHeaderStyle: CSSProperties = {
  position: 'sticky',
  top: 0,
  zIndex: 6,
  backgroundColor: 'var(--color-bg-surface)',
  paddingTop: 'var(--space-2)',
  paddingBottom: 'var(--space-3)',
  borderBottomWidth: '0.0625rem',
  borderBottomStyle: 'solid',
  borderBottomColor: 'var(--color-border-subtle)',
  boxShadow: 'var(--shadow-sm)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-1)',
};

const backlinkStyle: CSSProperties = {
  color: 'var(--color-accent-primary)',
  fontWeight: 'var(--font-weight-semibold)',
  fontSize: 'var(--font-size-sm)',
  cursor: 'pointer',
  background: 'none',
  border: 0,
  padding: 0,
  alignSelf: 'flex-start',
};

const idRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
};

// The rounded muted emoji tile (2.125rem square ≈ a compact 34-unit tile).
const emojiTileStyle: CSSProperties = {
  width: '2.125rem',
  height: '2.125rem',
  borderRadius: 'var(--radius-md)',
  backgroundColor: 'var(--color-bg-muted)',
  display: 'grid',
  placeItems: 'center',
  fontSize: 'var(--font-size-md)',
  flex: '0 0 auto',
};

// Inline rename (the title IS the rename control — no separate Name field).
// A token-styled button wrapping the name + a subtle pencil hint; tapping it
// swaps in an inline input. Mirrors the prototype's `.titlebtn` / `.titleinput`.
const titleButtonStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  background: 'none',
  border: 0,
  padding: 'var(--space-1)',
  margin: 'calc(-1 * var(--space-1))',
  borderRadius: 'var(--radius-md)',
  cursor: 'text',
  textAlign: 'left',
  font: 'inherit',
  color: 'inherit',
  minHeight: '2.5rem',
};

const titleTextStyle: CSSProperties = {
  margin: 0,
  // T47 Phase 4 — product detail page title (the product name h2) → Poppins display.
  fontFamily: 'var(--font-family-display)',
  fontSize: 'var(--font-size-lg)',
  fontWeight: 'var(--font-weight-bold)',
};

const titlePenStyle: CSSProperties = {
  color: 'var(--color-text-muted)',
  flex: '0 0 auto',
  fontSize: 'var(--font-size-base)',
};

const titleInputStyle: CSSProperties = {
  fontSize: 'var(--font-size-lg)',
  fontWeight: 'var(--font-weight-bold)',
  color: 'var(--color-text-primary)',
  backgroundColor: 'var(--color-bg-primary)',
  borderWidth: '0.0625rem',
  borderStyle: 'solid',
  borderColor: 'var(--color-accent-primary)',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--space-1) var(--space-2)',
  width: '100%',
  // T47 Phase 4 — match the title heading's Poppins display face so the inline
  // rename input doesn't switch face mid-edit.
  fontFamily: 'var(--font-family-display)',
};

const introStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
  lineHeight: 'var(--line-height-normal)',
};

const sectionStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-3)',
};

const labelStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-medium)',
  color: 'var(--color-text-secondary)',
};

const selectStyle: CSSProperties = {
  fontFamily: 'var(--font-family-sans)',
  fontSize: 'var(--font-size-base)',
  color: 'var(--color-text-primary)',
  backgroundColor: 'var(--color-bg-primary)',
  borderWidth: '0.0625rem',
  borderStyle: 'solid',
  borderColor: 'var(--color-border-strong)',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--space-2) var(--space-3)',
};

const checkboxRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-secondary)',
};

const errorStyle: CSSProperties = {
  color: 'var(--color-status-danger)',
  fontSize: 'var(--font-size-sm)',
};

const fieldColumnStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-1)',
};

const comingSoonStyle: CSSProperties = {
  ...fieldColumnStyle,
  padding: 'var(--space-3)',
  borderWidth: '0.0625rem',
  borderStyle: 'dashed',
  borderColor: 'var(--color-border-strong)',
  borderRadius: 'var(--radius-md)',
  marginTop: 'var(--space-3)',
};

const pillStyle: CSSProperties = {
  display: 'inline-block',
  padding: 'var(--space-1) var(--space-2)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-muted)',
  borderWidth: '0.0625rem',
  borderStyle: 'solid',
  borderColor: 'var(--color-border-strong)',
};

// The "How many to add" row inside the Basics group — a label/description on the
// left and the stepper on the right (prototype `.setrow`). The first Basics row,
// so no top rule (Basics' Size/How-often rows carry the hairline separators).
const basicsHowManyRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--space-3)',
};

const basicsHowManyLabelStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-primary)',
};

// The brief deep-link highlight. Token-only outline that fades after a beat.
const FLASH_CSS = `
[data-section].sec-flash {
  outline: 0.125rem solid var(--color-accent-primary);
  outline-offset: 0.25rem;
  border-radius: var(--radius-md);
  transition: outline-color 0.6s ease-out;
}
[data-section] { scroll-margin-top: 6.5rem; }
`;

/** Local editable form state — text fields are strings so empty clears to null. */
interface FormState {
  displayName: string;
  categoryId: string;
  notes: string;
  tags: string;
  imageUrl: string;
  defaultQuantity: string;
  allowSubstitutions: boolean;
  substitutionNotes: string;
  alwaysKeepStocked: boolean;
  expectedPriceValue: string;
  expectedPriceCurrency: PriceCurrency;
  expectedPriceNotes: string;
}

function toFormState(p: HouseholdProductWithDetails): FormState {
  return {
    displayName: p.display_name ?? '',
    categoryId: p.category_id ?? '',
    notes: p.notes ?? '',
    tags: (p.tags ?? []).join(', '),
    imageUrl: p.image_url ?? '',
    defaultQuantity: p.default_quantity != null ? String(p.default_quantity) : '1',
    allowSubstitutions: p.allow_substitutions ?? true,
    substitutionNotes: p.substitution_notes ?? '',
    alwaysKeepStocked: p.always_keep_stocked ?? false,
    expectedPriceValue: p.expected_price_value != null ? String(p.expected_price_value) : '',
    expectedPriceCurrency: p.expected_price_currency ?? 'CAD',
    expectedPriceNotes: p.expected_price_notes ?? '',
  };
}

/** Build the PATCH request + the optimistic detail-row changes from the form. */
function buildUpdate(form: FormState): {
  request: UpdateHouseholdProductRequest;
  changes: Partial<HouseholdProductWithDetails>;
} {
  const tags = form.tags
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
  const priceValText = form.expectedPriceValue.trim();
  const priceVal = priceValText ? Number(priceValText) : undefined;
  const qty = form.defaultQuantity.trim() ? Number(form.defaultQuantity) : undefined;
  const categoryId = form.categoryId.trim() || undefined;
  const notes = form.notes.trim() || undefined;
  const imageUrl = form.imageUrl.trim() || undefined;
  const subNotes = form.substitutionNotes.trim() || undefined;
  const priceNotes = form.expectedPriceNotes.trim() || undefined;

  // Optional keys are omitted (not set to `undefined`) so the request shape
  // satisfies `exactOptionalPropertyTypes`. The backend reads `!== undefined`
  // per field, so an omitted key leaves that column unchanged.
  const request: UpdateHouseholdProductRequest = {
    displayName: form.displayName.trim(),
    tags,
    allowSubstitutions: form.allowSubstitutions,
    alwaysKeepStocked: form.alwaysKeepStocked,
    expectedPriceCurrency: form.expectedPriceCurrency,
    ...(categoryId !== undefined ? { categoryId } : {}),
    ...(notes !== undefined ? { notes } : {}),
    ...(imageUrl !== undefined ? { imageUrl } : {}),
    ...(qty !== undefined ? { defaultQuantity: qty } : {}),
    ...(subNotes !== undefined ? { substitutionNotes: subNotes } : {}),
    ...(priceVal !== undefined ? { expectedPriceValue: priceVal } : {}),
    ...(priceNotes !== undefined ? { expectedPriceNotes: priceNotes } : {}),
  };

  const changes: Partial<HouseholdProductWithDetails> = {
    display_name: form.displayName.trim(),
    category_id: categoryId ?? null,
    notes: notes ?? null,
    tags,
    image_url: imageUrl ?? null,
    default_quantity: qty ?? 1,
    allow_substitutions: form.allowSubstitutions,
    substitution_notes: subNotes ?? null,
    always_keep_stocked: form.alwaysKeepStocked,
    expected_price_value: priceVal ?? null,
    expected_price_currency: form.expectedPriceCurrency,
    expected_price_notes: priceNotes ?? null,
  };

  return { request, changes };
}

export function HouseholdProductDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orchestrators = useOrchestrators();
  const query = useHouseholdProduct(id);
  const product = query.data;

  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Inline rename — the sticky-header title IS the rename control (no separate
  // Name field). Tapping it swaps the heading for an input seeded with the
  // current name; Enter/blur commits, Esc cancels, empty reverts.
  const [renaming, setRenaming] = useState(false);
  const [renameDraft, setRenameDraft] = useState('');
  const renameInputRef = useRef<HTMLInputElement | null>(null);

  // Publish the product name as the layover header title (the drawer is the
  // Product preferences surface, not "Settings"). Tracks any inline rename. The
  // hook no-ops on an empty/loading name so the AppShell keeps its fallback.
  usePublishLayoverTitle(product?.display_name);

  // Dynamic back — return to the caller (planning List or Products). Default
  // Products. The caller passes `?from=list` (or `products`).
  const from = searchParams.get('from');
  const backTarget = from === 'list' ? routes.home : routes.products;
  const backLabel = from === 'list' ? 'List' : 'Products';

  // Seed the form once the product loads. We only seed when the form is still
  // empty so an in-progress edit isn't clobbered by a background refetch.
  useEffect(() => {
    if (product && form === null) {
      setForm(toFormState(product));
    }
  }, [product, form]);

  // Deep-link to a section: once the product (and its child controls) have
  // rendered, scroll the `?section=` target into view and briefly highlight it.
  // The composed child sections (picker / settings) mount asynchronously after
  // their own queries resolve, so poll briefly until the target appears.
  const sectionParam = searchParams.get('section');
  useEffect(() => {
    if (!product || !isSectionKey(sectionParam)) return;
    let flashTimer = 0;
    let attempts = 0;
    const poll = window.setInterval(() => {
      attempts += 1;
      const el = document.querySelector<HTMLElement>(`[data-section="${sectionParam}"]`);
      if (el) {
        window.clearInterval(poll);
        if (el.tagName === 'DETAILS') (el as HTMLDetailsElement).open = true;
        el.scrollIntoView({ block: 'start', behavior: 'smooth' });
        el.classList.add('sec-flash');
        flashTimer = window.setTimeout(() => el.classList.remove('sec-flash'), 1300);
        // `size` special-case (prototype `scrollSettingsTo('size') →
        // openSizeSheet`): the Size editor is a bottom-sheet whose open state
        // lives in HouseholdProductSettings — open it by clicking the Basics
        // Size opener. (A snapshot-less product renders the empty message in
        // place of the opener, so there is nothing to click — correctly no-ops.)
        if (sectionParam === 'size') {
          const opener = el.querySelector<HTMLButtonElement>(
            '[data-testid="hp-settings-measurement-open"]',
          );
          opener?.click();
        }
      } else if (attempts >= 40) {
        // ~2s ceiling — give up rather than poll forever.
        window.clearInterval(poll);
      }
    }, 50);
    return () => {
      window.clearInterval(poll);
      if (flashTimer) window.clearTimeout(flashTimer);
    };
  }, [product, sectionParam]);

  // On desktop the prototype shows the three collapsed extras open by default —
  // there's room. Below the breakpoint they stay collapsed. Default-open them
  // once, after the form has seeded the <details> into the DOM.
  const [extrasDefaultOpen] = useState(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(min-width: 60rem)').matches
  );

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setSaved(false);
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  // "How many to add" — the Basics stepper edits the same `default_quantity` form
  // field the extras/Save path persists; floors at 1 (the prototype starts at 1).
  const parsedHowMany = Number(form?.defaultQuantity ?? '1');
  const howManyValue = Number.isFinite(parsedHowMany) && parsedHowMany >= 1 ? parsedHowMany : 1;
  const stepHowMany = (delta: number) => {
    update('defaultQuantity', String(Math.max(1, howManyValue + delta)));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form || !id) return;
    setFormError(null);

    if (!form.displayName.trim()) {
      setFormError('Name is required.');
      return;
    }
    const qty = Number(form.defaultQuantity);
    if (form.defaultQuantity.trim() && (!Number.isFinite(qty) || qty <= 0)) {
      setFormError('Default quantity must be a positive number.');
      return;
    }

    const { request, changes } = buildUpdate(form);
    setSaving(true);
    try {
      await orchestrators.HouseholdProducts.handleUpdateProduct({
        productId: id,
        changes,
        request,
      });
      setSaved(true);
    } catch (err) {
      setFormError(apiErrorMessage(err, 'Failed to save product'));
    } finally {
      setSaving(false);
    }
  };

  // Inline rename — open the editor seeded with the current name + focus it.
  const startRename = () => {
    if (!form) return;
    setRenameDraft(form.displayName);
    setRenaming(true);
    setFormError(null);
    // Focus + select on the next frame, once the input has mounted.
    window.setTimeout(() => {
      renameInputRef.current?.focus();
      renameInputRef.current?.select();
    }, 0);
  };
  const cancelRename = () => setRenaming(false);
  // Commit the rename: empty reverts (keep the prior name); a real change saves
  // through the same update path as the form, persisting `displayName` plus the
  // rest of the current form state.
  const commitRename = async () => {
    if (!renaming || !form || !id) return;
    const next = renameDraft.trim();
    setRenaming(false);
    if (!next || next === form.displayName.trim()) return; // empty reverts; no-op when unchanged
    const nextForm = { ...form, displayName: next };
    setForm(nextForm);
    setSaved(false);
    const { request, changes } = buildUpdate(nextForm);
    setSaving(true);
    try {
      await orchestrators.HouseholdProducts.handleUpdateProduct({
        productId: id,
        changes,
        request,
      });
      setSaved(true);
    } catch (err) {
      setFormError(apiErrorMessage(err, 'Failed to save product'));
    } finally {
      setSaving(false);
    }
  };
  const handleRenameKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void commitRename();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelRename();
    }
  };

  if (query.isLoading) {
    return (
      <div style={pageStyle} data-testid="product-detail-loading">
        <Spinner />
      </div>
    );
  }

  if (query.isError || !product) {
    return (
      <div style={pageStyle}>
        <Card padding="lg" data-testid="product-detail-error">
          <p>Could not load this product.</p>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(backTarget)}
            data-testid="product-detail-back"
          >
            ← Back to {backLabel.toLowerCase()}
          </Button>
        </Card>
      </div>
    );
  }

  if (!form) {
    return (
      <div style={pageStyle} data-testid="product-detail-loading">
        <Spinner />
      </div>
    );
  }

  return (
    <div style={pageStyle} data-testid="product-detail-page">
      <style>{FLASH_CSS}</style>

      {/* Sticky compact header — backlink + emoji tile + title + intro. */}
      <header style={stickyHeaderStyle} data-testid="product-detail-header">
        <button
          type="button"
          style={backlinkStyle}
          onClick={() => navigate(backTarget)}
          data-testid="product-detail-back"
        >
          ‹ {backLabel}
        </button>
        <div style={idRowStyle}>
          <span aria-hidden="true" style={emojiTileStyle}>
            📦
          </span>
          {/* The title IS the rename control — tap to edit in place. There is no
              separate "Name" field on this surface. Enter/blur commits, Esc
              cancels, empty reverts to the prior name. */}
          {renaming ? (
            <input
              ref={renameInputRef}
              className="hp-rename-input"
              style={titleInputStyle}
              value={renameDraft}
              onChange={(e) => setRenameDraft(e.target.value)}
              onBlur={() => void commitRename()}
              onKeyDown={handleRenameKeyDown}
              aria-label="Product name"
              data-testid="product-edit-display-name"
            />
          ) : (
            <button
              type="button"
              className="hp-rename-btn"
              style={titleButtonStyle}
              onClick={startRename}
              title="Tap to rename"
              aria-label={`Rename ${product.display_name}`}
              data-testid="product-rename-open"
            >
              <h2 style={titleTextStyle}>{product.display_name}</h2>
              <span style={titlePenStyle}>
                <Icon name="pencil" />
              </span>
            </button>
          )}
        </div>
        <p style={introStyle}>
          Most of this gently <strong>guides</strong> your suggestions — only what
          you mark <strong>Don&apos;t suggest</strong> is held back.
        </p>
      </header>

      {/* The fine-tune surface in prototype order (T27.2.7.10):
          1 · Basics (How many · Size · How often) — always open
          2 · Product preferences (the foldable picker)
          3 · Brand preferences (foldable)
          4 · Store preferences / where-to-buy (foldable)
          HouseholdProductSettings owns the order; the detail page injects the
          How-many stepper (it persists `default_quantity` through this form) and
          the picker so it lands between Basics and Brand preferences. */}
      {id && (
        <HouseholdProductSettings
          productId={id}
          basicsLeading={
            <div style={basicsHowManyRowStyle} data-testid="product-edit-howmany">
              <div style={basicsHowManyLabelStyle}>
                <div>How many to add</div>
                <div style={mutedStyle}>Default when this goes on your list</div>
              </div>
              <div style={stepperStyle}>
                <button
                  type="button"
                  style={stepperBtnStyle}
                  aria-label="Fewer"
                  onClick={() => stepHowMany(-1)}
                  data-testid="product-edit-howmany-decrement"
                >
                  −
                </button>
                <span style={stepperValueStyle} data-testid="product-edit-howmany-value">
                  {howManyValue}
                </span>
                <button
                  type="button"
                  style={stepperBtnStyle}
                  aria-label="More"
                  onClick={() => stepHowMany(1)}
                  data-testid="product-edit-howmany-increment"
                >
                  +
                </button>
              </div>
            </div>
          }
          productPreferencesSlot={id ? <SystemProductPicker productId={id} /> : null}
        />
      )}

      {/* Occasional extras — three `details.setgroup` blocks; side-by-side +
          open at desktop width (`.hp-extras-grid`). The product name is renamed
          inline in the sticky header above — there is no standalone Name field
          here (T36.3 / design-delta). `form.displayName` still lives in form
          state; the header rename writes it. */}
      <form onSubmit={handleSubmit} data-testid="product-detail-form">
        <div className="hp-extras-grid">
          {/* Quantity & packaging */}
          <details
            style={{ ...setgroupStyle, marginTop: 'var(--space-4)' }}
            open={extrasDefaultOpen}
            data-testid="product-edit-quantity-section"
            data-section="quantity"
          >
            <summary style={detailsSummaryStyle}>
              <span>Quantity &amp; packaging</span>
            </summary>
            <div style={sectionStyle}>
              {/* "How many to add" (default_quantity) now lives in the Basics
                  group at the top of the surface (T27.2.7.10). T36.3 / ADR-015 —
                  the product's measurement (`4 L`, `12 × 20 g`) is the resolved
                  snapshot from its linked SystemProduct, edited via the "Size"
                  picker in the Basics group. The legacy free-text unit-type /
                  size fields are retired. */}
              <label style={checkboxRowStyle}>
                <input
                  type="checkbox"
                  checked={form.allowSubstitutions}
                  onChange={(e) => update('allowSubstitutions', e.target.checked)}
                  data-testid="product-edit-allow-substitutions"
                />
                Allow substitutions
              </label>
              <Input
                label="Substitution notes"
                value={form.substitutionNotes}
                onChange={(e) => update('substitutionNotes', e.target.value)}
                hint="Optional."
                data-testid="product-edit-substitution-notes"
              />
              <label style={checkboxRowStyle}>
                <input
                  type="checkbox"
                  checked={form.alwaysKeepStocked}
                  onChange={(e) => update('alwaysKeepStocked', e.target.checked)}
                  data-testid="product-edit-always-keep-stocked"
                />
                Always keep stocked
              </label>
            </div>
          </details>

          {/* Expected price — advisory */}
          <details
            style={{ ...setgroupStyle, marginTop: 'var(--space-4)' }}
            open={extrasDefaultOpen}
            data-testid="product-edit-expected-price-section"
            data-section="price"
          >
            <summary style={detailsSummaryStyle}>
              <span>
                Expected price{' '}
                <span style={{ fontWeight: 'var(--font-weight-regular)', ...mutedStyle }}>
                  · advisory
                </span>
              </span>
            </summary>
            <div style={sectionStyle}>
              <Input
                label="You'd expect to pay around"
                type="number"
                min="0"
                step="0.01"
                value={form.expectedPriceValue}
                onChange={(e) => update('expectedPriceValue', e.target.value)}
                hint="Leave blank for none."
                data-testid="product-edit-expected-price-value"
              />
              <div style={fieldColumnStyle}>
                <label style={labelStyle} htmlFor="product-edit-currency">
                  Currency
                </label>
                <select
                  id="product-edit-currency"
                  style={selectStyle}
                  value={form.expectedPriceCurrency}
                  onChange={(e) =>
                    update('expectedPriceCurrency', e.target.value as PriceCurrency)
                  }
                  data-testid="product-edit-expected-price-currency"
                >
                  {CURRENCIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <Input
                label="Expected price notes"
                value={form.expectedPriceNotes}
                onChange={(e) => update('expectedPriceNotes', e.target.value)}
                hint="Optional."
                data-testid="product-edit-expected-price-notes"
              />
              <p style={mutedStyle}>
                A loose sanity check — not store truth. Real prices come from offers.
              </p>
            </div>
          </details>

          {/* More details — notes + tags + the category/image coming-soon stop. */}
          <details
            style={{ ...setgroupStyle, marginTop: 'var(--space-4)' }}
            open={extrasDefaultOpen}
            data-testid="product-edit-details-section"
            data-section="details"
          >
            <summary style={detailsSummaryStyle}>
              <span>More details</span>
            </summary>
            <div style={sectionStyle}>
              <Input
                label="Notes"
                value={form.notes}
                onChange={(e) => update('notes', e.target.value)}
                hint="e.g. the glass bottle, not plastic."
                data-testid="product-edit-notes"
              />
              <Input
                label="Tags"
                value={form.tags}
                onChange={(e) => update('tags', e.target.value)}
                hint="Comma-separated."
                data-testid="product-edit-tags"
              />

              {/* Category + image — a proper picker is not wired yet, so these
                  render as a "coming soon" stop (not raw id inputs). */}
              <div style={comingSoonStyle} data-testid="product-edit-category-image-comingsoon">
                <span style={labelStyle}>
                  Category &amp; image <span style={pillStyle}>coming soon</span>
                </span>
                <span style={mutedStyle}>
                  {product.category_name
                    ? `Category: ${product.category_name}.`
                    : 'No category set.'}{' '}
                  A category picker and product image are on the way.
                </span>
              </div>
            </div>
          </details>
        </div>

        {formError && (
          <div
            role="alert"
            style={{ ...errorStyle, marginTop: 'var(--space-3)' }}
            data-testid="product-edit-error"
          >
            {formError}
          </div>
        )}
        {saved && !formError && (
          <div style={{ ...mutedStyle, marginTop: 'var(--space-3)' }} data-testid="product-edit-saved">
            Saved.
          </div>
        )}

        <div
          style={{
            display: 'flex',
            gap: 'var(--space-2)',
            justifyContent: 'flex-end',
            marginTop: 'var(--space-4)',
          }}
        >
          <Button
            type="submit"
            variant="primary"
            loading={saving}
            disabled={saving}
            data-testid="product-edit-save"
          >
            Save changes
          </Button>
        </div>
      </form>
    </div>
  );
}
